import { useSearchParams } from 'react-router-dom';

export type BusinessLine = 'subscription' | 'license' | 'store';
export type CostKind = 'stripe_fee' | 'apple_fee' | 'goods' | 'carrier';

export const BUSINESS_LINES: { value: BusinessLine; label: string }[] = [
    { value: 'subscription', label: 'Suscripciones' },
    { value: 'license', label: 'Licencias' },
    { value: 'store', label: 'Tienda' },
];

export const COST_KINDS: { value: CostKind; label: string }[] = [
    { value: 'stripe_fee', label: 'Comisión Stripe' },
    { value: 'apple_fee', label: 'Comisión Apple' },
    { value: 'goods', label: 'Compra de producto' },
    { value: 'carrier', label: 'Transporte' },
];

export function lineLabel(line: string): string {
    return BUSINESS_LINES.find((l) => l.value === line)?.label ?? line;
}

export function costKindLabel(kind: string): string {
    return COST_KINDS.find((k) => k.value === kind)?.label ?? kind;
}

export function fmtEur(cents: number): string {
    return (cents / 100).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + '\u00a0€';
}

export function eurosToCents(value: string): number | null {
    const n = parseFloat(value.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
}

export function centsToEuroInput(cents: number): string {
    return (cents / 100).toFixed(2);
}

function monthBounds(now = new Date()): { from: string; to: string } {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const iso = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    return { from: iso(from), to: iso(to) };
}

export function boundsForMonth(month: string): { from: string; to: string } | null {
    const match = /^(\d{4})-(\d{2})$/.exec(month);
    if (!match) return null;
    const year = Number(match[1]);
    const monthIndex = Number(match[2]);
    if (monthIndex < 1 || monthIndex > 12) return null;
    const lastDay = new Date(year, monthIndex, 0).getDate();
    return {
        from: `${match[1]}-${match[2]}-01`,
        to: `${match[1]}-${match[2]}-${String(lastDay).padStart(2, '0')}`,
    };
}

export function periodMonthValue(from: string, to: string): string {
    if (!/^\d{4}-\d{2}-01$/.test(from)) return '';
    const bounds = boundsForMonth(from.slice(0, 7));
    if (!bounds || bounds.to !== to) return '';
    return from.slice(0, 7);
}

export function useOperationsPeriod() {
    const [params, setParams] = useSearchParams();
    const defaults = monthBounds();
    const from = params.get('from') || defaults.from;
    const to = params.get('to') || defaults.to;

    function setPeriod(nextFrom: string, nextTo: string) {
        const next = new URLSearchParams(params);
        next.set('from', nextFrom);
        next.set('to', nextTo);
        setParams(next, { replace: true });
    }

    return { from, to, setPeriod };
}

export function PeriodFilter({
    from,
    to,
    onChange,
}: {
    from: string;
    to: string;
    onChange: (from: string, to: string) => void;
}) {
    const month = periodMonthValue(from, to);

    function jumpToMonth(value: string) {
        const bounds = boundsForMonth(value);
        if (bounds) onChange(bounds.from, bounds.to);
    }

    return (
        <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Mes</span>
                <input
                    type="month"
                    value={month}
                    onChange={(e) => jumpToMonth(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                />
            </label>
            <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Desde</span>
                <input
                    type="date"
                    value={from}
                    onChange={(e) => onChange(e.target.value, to)}
                    className="px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                />
            </label>
            <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Hasta</span>
                <input
                    type="date"
                    value={to}
                    onChange={(e) => onChange(from, e.target.value)}
                    className="px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                />
            </label>
        </div>
    );
}
